import React, {useRef, useState, useLayoutEffect, useEffect} from 'react';
import {useIntl} from 'umi';
import classNames from "classnames";
import {useMemoizedFn} from "ahooks";
import {Tabs, Input, Bubble, Button, Modal} from "tea-component";
import Sortable from 'sortablejs';
import SvgIcon from "@home/components/SvgIcon";
import "./index.scss";

const { TextArea } = Input;

export const EditInput = (props: any) => {
  const {onHidden, children, errorContent, defaultValue, onChange, checkValue, overlayClassName, type = 'input', maxLength, value, hasClear = false, ...resetProps} = props;
  const [errorVisible, setErrorVisible] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || defaultValue);
  const inputRef = useRef<any>(null);
  
  // TODO 重复命名提示的交互????, 展示之后什么时候消失??
  const onBlur = async (newValue: string) => {
    if (newValue === defaultValue || (!newValue?.trim())) {
      onHidden?.();
      return;
    }
    
    // 重名校验
    const isSuccess = await checkValue?.(newValue, defaultValue) ?? true;
    
    if (isSuccess) {
      onChange(newValue);
      onHidden?.();
    } else {
      setErrorVisible(true);
    }
    
  };

  const onClear = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setCurrentValue('');
    setTimeout(() => {
      inputRef.current?.focus?.();
      inputRef.current?.select?.();
    }, 0);
  };

  useEffect(() => {
    setCurrentValue(value || defaultValue);
  },[value, defaultValue]);
  
  useLayoutEffect(() => {
  
    // 当显示input框时, 自动获取焦点, 且文本默认全选
    inputRef.current.focus();
    inputRef.current.select();
    
  }, []);

  const Component = type === 'input' ? Input : TextArea;
  
  return (<Bubble
    {...resetProps}
    content={errorContent}
    visible={errorVisible}
    overlayClassName={classNames(overlayClassName,{
      'dbl-edit-input-bubble-error': errorVisible
    })}
  >
    <div className={classNames('dbl-edit-input-wrap', { 'dbl-edit-input-clear-wrap': hasClear })}>
      <Component
        ref={inputRef}
        maxLength={maxLength}
        defaultValue={defaultValue}
        value={currentValue}
        onBlur={(event)=> {
          // 失去焦点
          event.stopPropagation();
          onBlur(event.target.value);
        }}
        onChange={(value)=> {
          setCurrentValue(value);
        }}
        onPressEnter={(value, event) => {
          // 点击回车
          event.stopPropagation();
          onBlur(value);
        }}
        onClick={(event) => event.stopPropagation()}
        className={classNames('dbl-edit-input', {
          'dbl-edit-input-error': errorVisible
        })}
      />
      {hasClear && currentValue?.length > 0 && (
        <div
          className='dbl-edit-input-clear-wrap-icon'
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClear}
        >
          <SvgIcon type="clear-icon" />
        </div>
      )}
    </div>
  </Bubble>);
};


export const TabEditItem = (props: any) => {
  const { label, id, onDeleteClick, onRenameTab, checkName , placementOffset = 0 , errorContent, placement = 'bottom', overlayClassName = '', onHidden, type = 'input', hasClear = false } = props;
  const { formatMessage } = useIntl();
  const [show, setShow] = useState(false);
  
  return show ? (
    <EditInput
      type={type}
      value={label}
      defaultValue={label}
      hasClear={hasClear}
      onHidden={() => {
        onHidden?.(); 
        setShow(false);
      }}
      onChange={(value) => {
        onRenameTab({ id, label: value });
      }}
      checkValue={checkName}
      errorContent={errorContent || formatMessage({ id: 'dbl_create_dashboard_tab_duplicate_name' })}
      placement={placement}
      overlayClassName={overlayClassName}
      // placementOffset={1}
    />
  ) : (
    (<div
      className={'dbl-tab-edit'}
    >
  
      <div className={'dbl-tab-svg-icon dbl-tab-edit-drag-icon'}>
        <SvgIcon type="drag-icon"/>
      </div>
      <div
        className={'dbl-tab-item-name'}
        onDoubleClick={(event) => {
          event.stopPropagation();
          // 双击会导致 页签的切换，金龙表示可以接受
          setShow(true);
        }}
      >
        {label}
      </div>
      <Bubble
        content={formatMessage({ id: 'dbl_create_dashboard_tab_rename' })}
        dark
        placement={'bottom'}
        className={'dbl-tab-edit-icon-bubble'}
        placementOffset={placementOffset}
      >
        <div className={'dbl-tab-svg-icon'}  onClick={(event) => {
          event.stopPropagation();
          setShow(true);
        }}>
          <SvgIcon type="pencil-m" className={'dbl-tab-edit-icon'} />
        </div>
      </Bubble>
      <Bubble
        content={formatMessage({ id: 'dbl_create_dashboard_tab_delete' })}
        dark
        placement={'top'}
        className={'dbl-tab-edit-icon-bubble'}
        placementOffset={placementOffset}
      >
        <div className={'dbl-tab-svg-icon'} onClick={(event) => {
          event.stopPropagation();
          onDeleteClick?.({ label, id });
        }}>
          <SvgIcon type="trash-can-icon" />
        </div>
      </Bubble>
    </div>)
  );
};



const TabsEdit = (props: any) => {
  const { tabs, activeId, onActive, onDeleteTab, onRenameTab, onAddTab, className, onSort } = props;
  const { formatMessage } = useIntl();
  const [deleteVisible, setDeleteVisible] = useState(false);
  const flagRef = useRef(false);
  const sortTabsRef = useRef([]);
  const ref = useRef(null);
  const delTabRef = useRef({});
  const checkName = useMemoizedFn((newName) => !tabs.some(tab => tab.label === newName));
  sortTabsRef.current = tabs;
  
  useLayoutEffect(() => {

    // flagRef.current 判断用于防止多次调用 Sortable.create
    if (!flagRef.current && tabs.length > 0 && ref.current) {
      // ref.current.querySelector('.tea-tabs__tablist') 获取的是页签的下的ul元素，li是被拖拽排序的元素
      Sortable.create(ref.current.querySelector('.tea-tabs__tablist'), {
        group: {
          name: 'tabDrag',
          pull: false,
          put: false,
        },
        animation: 200,
        sort: true,
        // chosenClass: 'dbl-tab-edit-item-ghost',
        // ghostClass: 'dbl-tab-edit-item-ghost',
        // dragClass: 'dbl-tab-edit-item-drag',
        handle: '.dbl-tab-edit-drag-icon',
        // 在这里直接写排序方法，无法获取到最新的tabs, 需要通过sortTabsRef获取到最新的tabs
        onSort: (event: { oldIndex: number; newIndex: number; }) => {
          const {oldIndex, newIndex} = event;
          const newTabs = [...sortTabsRef.current];

          const dragItem = newTabs.splice(oldIndex, 1)[0];
          newTabs.splice(newIndex, 0, dragItem);
  
          onSort(newTabs);
        },
      });
      flagRef.current = true;
    }

  }, [tabs.length]);
  
  return (<>
    <Tabs
      ref={ref}
      className={classNames('dbl-tabs-edit', className)}
      tabs={tabs}
      activeId={activeId}
      onActive={onActive}
      tabBarRender={(children, tab) => (
        <a>
          <TabEditItem
            label={tab.label}
            id={tab.id}
            onDeleteClick={(delTab) => { delTabRef.current = delTab;setDeleteVisible(true)  }}
            onRenameTab={onRenameTab}
            checkName={checkName}
          />
        </a>
      )}
      addon={(
        <Bubble
          content={formatMessage({ id: 'dbl_create_dashboard_tab_add' })}
          dark
          placement={'top'}
          className={'dbl-tab-edit-icon-bubble'}
          placementOffset={[1, 8]}
        >
          <Button className={'dbl-tab-add-icon'} onClick={onAddTab} type="icon" icon="plus" />
        </Bubble>
      )}
    />
    <Modal
      disableEscape
      visible={deleteVisible}
      caption={(<div className={'dbl-delete-bubble-header'}>
        <SvgIcon type={'alert-yellow'}/>
        <div dangerouslySetInnerHTML={{__html: formatMessage({ id: 'dbl_create_dashboard_tab_delete_title' })}}/>
      </div>)}
      onClose={() => { setDeleteVisible(false) }}
      className={'dbl-tab-delete-modal'}
    >
      <Modal.Body>
        <div className={'dbl-delete-bubble-body'}>
          <div dangerouslySetInnerHTML={{__html: formatMessage({ id: 'dbl_create_dashboard_tab_delete_desc' }, { name: `<b>${delTabRef.current?.label}</b>` })}}/>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={() => {
          onDeleteTab?.(delTabRef.current);
          setDeleteVisible(false);
        }}>
          {formatMessage({id: 'dbl_common_confirm'})}
        </Button>
        <Button type="weak" onClick={() => { setDeleteVisible(false) }}>
          {formatMessage({id: 'dbl_common_cancel'})}
        </Button>
      </Modal.Footer>
    </Modal>
  </>
  );
};

export default React.memo(TabsEdit);